/**
 * The Share panel's role-toggle truth (ticket #400). The rule both
 * platforms render by:
 *   1. the live link's role IS the selection — the Viewer default
 *      exists only where no choice and no link do;
 *   2. the owner's deliberate tap is an override tied to the link it
 *      was made against (or, before any link exists, it is the pending
 *      Generate request and displays as such);
 *   3. a regenerated (new-token) link re-syncs the display to the new
 *      link's actual role — no stale override outlives its link.
 *
 * The module also carries join precedence (ticket #401): what an
 * invite link does to someone who is ALREADY a member — upgrade-only,
 * resolved through the shared role ordering.
 */

import { ROLE_ORDER, roleAtLeast, type Role } from "./index";

export type InviteLinkRole = "viewer" | "editor";

/** The live invite link, as the share query returns it (null = none yet). */
export interface LiveInviteLink {
  token: string;
  role: InviteLinkRole;
}

/** The owner's deliberate toggle, tied to the link it was made against.
 * A null linkToken = chosen while no link existed: it governs the next
 * generate, then yields to the minted link's own role. */
export interface InviteRoleChoice {
  linkToken: string | null;
  role: InviteLinkRole;
}

export function selectedInviteRole(
  liveLink: LiveInviteLink | null | undefined,
  choice: InviteRoleChoice | null,
): InviteLinkRole {
  if (choice !== null) {
    // A choice tied to the live link governs until the link is replaced.
    if (
      choice.linkToken !== null &&
      liveLink != null &&
      choice.linkToken === liveLink.token
    ) {
      return choice.role;
    }
    // A pre-link choice governs while no link exists — it is the
    // pending Generate request, and hiding it would snap the toggle
    // back mid-choose.
    if (choice.linkToken === null && liveLink == null) {
      return choice.role;
    }
  }
  return liveLink?.role ?? "viewer";
}

/**
 * Join precedence (ticket #401): an invite link outranks a Member's
 * current role exactly when it grants MORE — upgrade-only, resolved
 * through the shared role ordering, never a hand-rolled comparison.
 * invites:join consumes the decision server-side (the active-member
 * arm writes only on `upgrades`); the join screens derive their copy
 * from joinPreviewCopy so both platforms describe the same outcome.
 * Owner/admin are naturally spared: link roles are viewer|editor
 * only, so "at least the link's role" already holds for them. The
 * accepted consequence (ADR-0010 token-as-authority) is that a
 * manually demoted member can re-upgrade with a still-live link — the
 * owner's remedy is regenerating, which kills the old URL.
 */
export interface JoinUpgrade {
  /** True when the join WRITES a role onto the active member. */
  upgrades: boolean;
  /** The role the member holds after the join (unchanged when none). */
  resultingRole: Role;
}

export function joinUpgrade(
  currentRole: Role,
  linkRole: InviteLinkRole,
): JoinUpgrade {
  // Member.role arrives as a raw string (the #399 law). The rank now
  // refuses a non-Role string itself (#820), but this door stays
  // load-bearing for the RETURN SHAPE: past it, a garbage role reads
  // "not an upgrade" from roleAtLeast and would fall through to
  // `upgrades: true` — the corrupt row must return UNCHANGED instead.
  if (!ROLE_ORDER.includes(currentRole)) {
    return { upgrades: false, resultingRole: currentRole };
  }
  if (roleAtLeast(currentRole, linkRole)) {
    return { upgrades: false, resultingRole: currentRole };
  }
  return { upgrades: true, resultingRole: linkRole };
}

/**
 * The join screen's role line (ticket #401): what the page says BEFORE
 * joining, given the signed-in visitor's own membership (undefined
 * when signed out, or signed in but not an active member) and the
 * link's role. null = the fresh "You'll join as …" line both platforms
 * already render. An active member reading a HIGHER link sees the
 * upgrade spelled out — it used to silently no-op — and anyone the
 * link cannot lift reads as already-a-member.
 */
export function joinPreviewCopy(
  callerRole: Role | undefined,
  linkRole: InviteLinkRole,
): string | null {
  if (callerRole === undefined) return null;
  const upgrade = joinUpgrade(callerRole, linkRole);
  if (!upgrade.upgrades) return "You're already a member of this list.";
  return `You're already on this list as ${callerRole} — joining will upgrade you to ${linkRole}${inviteRoleDetail(linkRole)}.`;
}

/**
 * The parenthetical that follows a link role in join copy — "(view-only)"
 * for a viewer link, "(can add and edit items)" for an editor one — so
 * the fresh-join line and the upgrade line say the same words on both
 * platforms (the fresh line is per-platform JSX/template around it).
 */
export function inviteRoleDetail(linkRole: InviteLinkRole): string {
  return linkRole === "viewer" ? " (view-only)" : " (can add and edit items)";
}
